import { CircleHelp } from "lucide-react";
import { type KeyboardEvent, type ReactElement, useState } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * A "?" beside a label that opens one sentence (D43: a question mark rather than a paragraph).
 *
 * Not a <button>: inside a read-only form the disabled fieldset would switch it off, and the
 * help is needed most by whoever may only read. A span with the button role cannot be disabled
 * by a fieldset and cannot submit a form; Enter and Space open it like a button. Beside the
 * label rather than inside it, so a click on the label still reaches the field. The hit area
 * is 44 px around a small mark. The focus stays on the mark, Tab goes on through the form,
 * Escape or a click elsewhere closes the sentence.
 */
export function HelpTip({
  topic,
  children,
}: {
  /** Names the field in the button's label: "Hilfe zu Freigabefrist". */
  readonly topic: string;
  readonly children: string;
}): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          aria-label={`Hilfe zu ${topic}`}
          className="relative inline-flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground after:absolute after:-inset-2.5 after:content-[''] hover:text-foreground focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring"
          onKeyDown={(event: KeyboardEvent<HTMLSpanElement>) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setOpen((current) => !current);
            }
          }}
        >
          <CircleHelp aria-hidden="true" className="size-4" />
        </span>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label={`Hilfe zu ${topic}`}
        className="w-auto max-w-xs text-sm"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
